<template>
  <div
    id="projectContractorInfo"
    class="project-contractor-info"
  >
    <div
      v-if="(updatorName && updatedTime) || canEdit"
      class="contractor-info-head clearfix"
    >
      <div
        v-if="updatorName && updatedTime"
        class="updator-info"
      >
        {{ $t('lastUpdatedBy', [updatorName, updatedTime]) }}
      </div>
      <div
        v-if="canEdit"
        class="contractor-info-buttons"
      >
        <HKLButton
          v-if="!isEditing"
          class="no-min-width"
          action="edit"
          color="darkblue"
          @click="enableEdit"
        />
        <HKLButton
          v-if="isEditing"
          v-hkldreset
          class="no-min-width"
          action="cancel"
          color="darkblue"
          @click="cancel"
        />
        <HKLButton
          v-if="isEditing"
          v-hkldreset
          class="no-min-width"
          action="save"
          color="orange"
          @click="doSave"
        />
      </div>
    </div>

    <div
      class="contractor-instruction"
      v-html="$t(`termsAndConditions.${regionName}.tenancy.contractorInfoRemark`)"
    />

    <div class="main-contractor-card">
      <ContractorFields
        :contractor="currentMainContractor"
        :inputRequireHighlight="inputRequireHighlight"
        :isEditing="isEditing"
        :canEdit="canEditMainCon"
      />
    </div>
    <div
      v-for="(contractor, index) in (currentContractors || [])"
      :key="index"
      class="sub-contractor-card"
    >
      <ContractorFields
        :contractor="contractor"
        :canEdit="canEdit"
        :inputRequireHighlight="inputRequireHighlight"
        :isEditing="isEditing"
        :contractorIndex="index"
      />
    </div>

    <div
      v-if="isEditing && canEdit"
      class="add-contractor-wrapper"
    >
      <button
        type="button"
        class="btn add-contractor-btn"
        @click="addContractor"
      >{{ $t('contact.addSubContractor') }}</button>
    </div>
    <ContractorsInviteModalVue
      ref="contractorsInviteModal"
      :canInvite="canEdit"
    />
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { computed, getCurrentInstance, unref } from 'vue-demi'
import { getKey } from '@/api/common/request'
import { validateDlpInCompleted } from '@/api/projects/util'
import HKLButton from '@/controls/Button/HKLButton.vue'
import { useRefModal } from '@/controls/Modal/modal.hook'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { utils as formUtils } from '@/plugin/InputChange'
import { useStoreGetters } from '@/store/index'
import mutationTypes from '@/store/mutation-types'
import { aclHelper, tmsPermsTypes } from '@/utils/aclHelper'
import dateUtil from '@/utils/dateUtil'
import i18n from '@/utils/i18n'
import { projectStatus } from '@/utils/locale'
import notification from '@/utils/notification'
import { useIsProjectRepresentative } from '@/pages/projects/projectInfo'
import ContractorFields from './ContractorFields'
import ContractorsInviteModalVue from './ContractorsInviteModal.vue'
import { ContractorExample, RepAccessTypes } from './consts'
import { useShouldInviteMainContractors, useShouldInviteSubContractors } from './contractors.hook'
import { getUpdatorName } from './utils'

const FORM_SRC_CONTRACTOR_INFO = 'contractorInfo'

export default {
  components: {
    HKLButton,
    ContractorsInviteModalVue,
    ContractorFields,
  },
  setup () {
    let returnData = {}
    const { proxy } = getCurrentInstance()
    const { currentMainContractor, currentContractors } = useStoreGetters('projectContractors', ['currentMainContractor', 'currentContractors'])
    const { modal: contractorsInviteModal, open: openInviteModal } = useRefModal()
    const shouldInvitedMainContractors = useShouldInviteMainContractors(currentMainContractor)
    const shouldInvitedSubContractors = useShouldInviteSubContractors(currentContractors)
    const shouldShowInvitedModal = computed(() => {
      return unref(shouldInvitedMainContractors).length || unref(shouldInvitedSubContractors).length
    })
    async function doSave () {
      const isSuccess = await proxy.save()
      if (isSuccess && unref(shouldShowInvitedModal)) {
        openInviteModal()
      }
    }
    returnData = Object.assign(returnData, {
      shouldInvitedMainContractors,
      shouldInvitedSubContractors,
      contractorsInviteModal,
      doSave,
    })
    const isProjectRepresentative = useIsProjectRepresentative()
    const canEdit = computed(() => {
      // Firstly, check if it's withdrawn
      if (proxy.isWithdrawn) return false

      // For Tenant project, main user, check completed or not, otherwise, check proxy.canUpdateTmsProjects
      if (proxy.isTms) return proxy.isMainUser ? !proxy.isCompleted : proxy.canUpdateTmsProjects

      // For Hkld project
      return (proxy.isMainUser || proxy.isCmsProjectPic || unref(isProjectRepresentative)) && proxy.cmsProjectEditable
    })
    const canEditMainCon = computed(() => {
      return unref(isProjectRepresentative) || (proxy.isTms ? proxy.canUpdateTmsProjects : proxy.canEditMainContractorInfo)
    })
    returnData = Object.assign(returnData, {
      canEdit,
      canEditMainCon,
    })
    return returnData
  },
  data () {
    return {
      keep: null,
      isEditing: false,
      inputRequireHighlight: false,
      vendorUsers: [],
      projectId: '',
    }
  },
  watch: {
    currentMainContractor: {
      deep: true,
      handler () {
        this.syncDirtyFromKeep()
      },
    },
    currentContractors: {
      deep: true,
      handler () {
        this.syncDirtyFromKeep()
      },
    },
  },
  computed: {
    ...mapGetters('ccd', {
      canAccessAllProjects: 'canAccessAllProjects',
      tenancyAccounts: 'accounts',
    }),
    ...mapGetters('projects', ['currentProject']),
    ...mapGetters('tenantProject', {
      accounts: 'accountsReturnEmptyListIfEmpty',
      currentAccount: 'currentAccount',
      isWithdrawn: 'isWithdrawn',
      isCompleted: 'isCompleted',
      isNormalAccount: 'isNormalAccount',
    }),
    ...mapGetters('projectContractors', ['currentMainContractor', 'currentContractors', 'isMainUser', 'isTms', 'isSubUser', 'myContractor']),
    ...mapGetters('user', ['currentUser', 'isEngineer', 'currentUserContact', 'isContractor']),
    ...mapGetters('regions', ['regionName']),
    cmsProjectEditable () {
      return !this.isTms && (lodash.get(this.currentProject, 'status') === projectStatus.ACTIVE || validateDlpInCompleted(this.currentProject))
    },
    isCmsProjectPic () {
      return !this.isTms && (this.isEngineer ? this.currentUser.id === deep(this.currentProject, 'manager.id') : aclHelper.hasProjectsAccess(this.isContractor) && !this.isSubUser)
    },
    canEditMainContractorInfo () {
      return (this.isCmsProjectPic || this.isMainUser) ? this.hasProjectEntitlement : this.currentProject.vendorId === this.currentUser.externalId
    },
    canEngineerCreateUpdateProjects () {
      return aclHelper.getAccess(tmsPermsTypes.project.createUpdate)
    },
    canUpdateTmsProjects () {
      // Firstly, check if it's withdrawn
      if (this.isWithdrawn) return false

      // For engineer, check permission
      if (this.isEngineer) return this.canEngineerCreateUpdateProjects

      // For tenant users / tenancy accounts, check project completed or not first
      if (this.isCompleted) return false

      return (this.canAccessAllProjects || (this.currentAccount && this.isNormalAccount))
    },
    updatorName () {
      return getUpdatorName(this.latest.updator || {}, this.latest, this.tenancyAccounts)
    },
    hasProjectEntitlement () {
      return aclHelper.hasProjectsAccess(this.isContractor) && (this.isEngineer || this.currentUser.externalId === this.currentProject.vendorId)
    },
    latest () {
      return lodash.orderBy([
        this.currentMainContractor,
        ...this.currentContractors,
      ], ['updatedAt'], ['desc'])[0]
    },
    updatedTime () {
      return dateUtil.format(this.latest.updatedAt, 'DD MMM YY HH:mm')
    },
  },
  mounted () {
    this.projectId = this.$router.currentRoute.params.id
  },
  methods: {
    ...mapActions('projectContractors', [
      'getProjectContractors',
      'updateProjectContractors',
    ]),
    ...mapMutations('projectContractors', {
      updateContractorField: mutationTypes.UPDATE_PATH_FIELD,
    }),
    ...mapActions('tenantProject', ['updateAccounts', 'loadTenantProject']),
    ...mapMutations('tenantProject', {
      updateProjectAccounts: mutationTypes.TENANT_PROJECT_ACCOUNTS,
    }),
    enableEdit () {
      this.isEditing = true
      this.inputRequireHighlight = false
      this.keep = lodash.cloneDeep({ mainContractor: this.currentMainContractor, contractors: this.currentContractors })
      formUtils.resetDirty(FORM_SRC_CONTRACTOR_INFO)
    },
    hasUnsavedChanges () {
      if (!this.isEditing || !this.keep) return false
      return !lodash.isEqual(
        { mainContractor: this.currentMainContractor, contractors: this.currentContractors },
        this.keep,
      )
    },
    syncDirtyFromKeep () {
      if (!this.isEditing || !this.keep) return
      if (this.hasUnsavedChanges()) {
        formUtils.setDirty(FORM_SRC_CONTRACTOR_INFO)
      } else {
        formUtils.resetDirty(FORM_SRC_CONTRACTOR_INFO)
        formUtils.resetDirty()
      }
    },
    async save () {
      if (this.validate()) {
        this.isEditing = false
        if (this.isTms) {
          this.syncMainContact()
          await this.updateAccounts(false)
          await this.updateProjectContractors({ isTenantProject: true })
          await this.loadTenantProject(this.projectId)
        } else {
          await this.updateProjectContractors({ isTenantProject: false })
          await this.getProjectContractors(this.projectId)
        }
        formUtils.resetDirty(FORM_SRC_CONTRACTOR_INFO)
        formUtils.resetDirty()
        return true
      }
      return false
    },
    cancel () {
      this.updateContractorField({ path: ['mainContractor'], newValue: this.keep.mainContractor })
      this.updateContractorField({ path: ['contractors'], newValue: this.keep.contractors })
      this.isEditing = false
      this.keep = null
      formUtils.resetDirty(FORM_SRC_CONTRACTOR_INFO)
      formUtils.resetDirty()
    },
    async addContractor () {
      const contractorList = lodash.cloneDeep(this.currentContractors)

      const id = await getKey()
      let emptyContractor = lodash.cloneDeep(ContractorExample)
      emptyContractor.id = id
      contractorList.push(emptyContractor)
      this.updateContractorField({ path: ['contractors'], newValue: contractorList })
      this.syncDirtyFromKeep()
    },
    validate () {
      this.inputRequireHighlight = false
      fusionInputCheckUtil.resetAllFields()
      fusionInputCheckUtil.validateAllFields(undefined, '#projectContractorInfo')
      const allContractors = this.currentContractors.concat(this.currentMainContractor)
      const isCompanyInfoError = allContractors.some(contractor => !contractor.type || !deep(contractor, 'company.name'))
      const isSamePhoneNumbers = allContractors.some(contractor => (contractor.primaryContact.phone !== '' && contractor.primaryContact.phone === contractor.primaryContact.altPhone) || (contractor.contacts && contractor.contacts.some(contact => contact.phone !== '' && contact.phone === contact.altPhone)))
      if (isCompanyInfoError || isSamePhoneNumbers) {
        if (isSamePhoneNumbers) notification.fireNotification(false, i18n.t('hint.inputTip.phoneShouldBeDifferent'))
        this.inputRequireHighlight = true
        this.$forceUpdate()
      }

      const errorNodes = $('.project-contractor-info .required-error-highlight')

      return errorNodes.length < 1 && fusionInputCheckUtil.isAllFieldsValid(`#projectContractorInfo`) && !isCompanyInfoError && !isSamePhoneNumbers
    },
    findOrInsertAdmin (accountsList, primaryContact) {
      let adminAccount = accountsList.find(account => account.TenantProjectAccount.isContractorAdmin)
      if (!adminAccount) {
        adminAccount = {
          TenantProjectAccount: { isContractorAdmin: true },
        }
        accountsList.unshift(adminAccount)
      }
      return adminAccount
    },
    syncContactInfo (adminAccount, primaryContact) {
      adminAccount.username = primaryContact.email
      adminAccount.TenantProjectAccount.contactFirstName = primaryContact.firstName
      adminAccount.TenantProjectAccount.contactLastName = primaryContact.lastName
      adminAccount.TenantProjectAccount.phone = primaryContact.phone
      adminAccount.TenantProjectAccount.altPhone = primaryContact.altPhone
      adminAccount.TenantProjectAccount.accessType = RepAccessTypes.CONTRACTOR
    },
    syncMainContact () {
      const primaryContact = lodash.get(this.currentMainContractor, 'primaryContact')
      if (!primaryContact) {
        return
      }

      const accountsList = lodash.cloneDeep(this.accounts)
      const adminAccount = this.findOrInsertAdmin(accountsList, primaryContact)
      this.syncContactInfo(adminAccount, primaryContact)
      this.updateProjectAccounts({value: accountsList})
    },
  },
}
</script>

<style lang="scss" scoped>
.project-contractor-info {
  .contractor-info-head {
    padding-bottom: 15px;
    .contractor-info-buttons {
      float: right;
    }
    .updator-info {
      width: calc(100% - 160px);
      float: left;
      color: #878787;
      font-style: italic;
      line-height: 2.86;
    }
  }

  .contractor-instruction {
    margin-bottom: 18px;
  }

  .main-contractor-card, .sub-contractor-card {
    margin-bottom: 20px;
  }

  .add-contractor-wrapper {
    text-align: center;
    .add-contractor-btn {
      width: 440px;
      height: 43px;
      border-radius: 23.5px;
      background-color: #6e7a91;
      font-family: Verdana;
      font-size: 14px;
      font-weight: bold;
      color: #ffffff;
      cursor: pointer;
    }
  }
}
</style>
